import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Download, Edit } from "lucide-react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import PatientPDF from "../../pages/admin/PatientPDF";
import type { Consultation, MedicalRecord } from "../../types";
import { usePatientHistory } from "../../hooks/usePatientHistory";

// Define union type for patientInfo
type PatientInfoType = Consultation | MedicalRecord | null;

const PatientAccountView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const isAccountContext = location.pathname.includes("/account/patient/");
  const { consultations, constantes, medicalRecords, isLoading: loading, error: queryError } = usePatientHistory(id, isAccountContext);

  const backPath = isAccountContext
    ? "/admin/dashboard/account/patient"
    : "/admin/dashboard/patient";
  const editPath = isAccountContext
    ? `/admin/dashboard/account/patient/edit/${id}`
    : `/admin/dashboard/patient/edit/${id}`;

  const error = queryError ? (queryError as any).message : null;

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "Non spécifié";
    try {
      return new Date(dateString).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "Date invalide";
    }
  };

  const renderFilePreview = (url: string | null): React.ReactElement => {
    if (!url) return <p className="text-gray-400 italic">Aucun fichier</p>;
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    const isPdf = /\.pdf$/i.test(url);
    if (isImage) {
      return (
        <img
          src={url}
          alt="Aperçu fichier"
          className="max-w-full max-h-64 rounded-lg border object-cover"
        />
      );
    } else if (isPdf) {
      return (
        <iframe
          src={url}
          title="PDF Preview"
          className="w-full h-64 rounded-lg border"
        />
      );
    } else {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          Télécharger le fichier
        </a>
      );
    }
  };

  if (loading) {
    return (
      <LoadingScreen
        message="Chargement des données du patient..."
        role="admin"
      />
    );
  }

  if (error || (consultations.length === 0 && medicalRecords.length === 0)) {
    return (
      <ResponseMessage
        message={error || "Aucune donnée trouvée pour ce patient."}
        role="admin"
      />
    );
  }

  // Use either consultation or medical record as primary patient info
  const patientInfo: PatientInfoType =
    consultations[0] || medicalRecords[0] || null;

  // Extract safe properties for rendering
  let patientEmail = "";
  let patientGender = "";
  let patientAddress = "";
  if (patientInfo) {
    if ("email" in patientInfo && patientInfo.email)
      patientEmail = patientInfo.email as string;
    if ("gender" in patientInfo && patientInfo.gender)
      patientGender = patientInfo.gender as string;
    if ("address" in patientInfo && patientInfo.address)
      patientAddress = patientInfo.address as string;
    else if ("adresse" in patientInfo && (patientInfo as any).adresse)
      patientAddress = (patientInfo as any).adresse as string;
  }

  // Helper to get patient name
  const getPatientName = (): string => {
    if (!patientInfo) return "Nom non disponible";
    if ("fullName" in patientInfo && patientInfo.fullName)
      return patientInfo.fullName;
    if (
      "firstName" in patientInfo &&
      patientInfo.firstName &&
      "lastName" in patientInfo &&
      patientInfo.lastName
    ) {
      return `${patientInfo.firstName} ${patientInfo.lastName}`;
    }
    if ("patientFullName" in patientInfo && patientInfo.patientFullName)
      return (patientInfo.patientFullName as string) || "Nom non disponible";
    return "Nom non disponible";
  };

  // Helper to get patient ID
  const getPatientId = (): string => {
    if (!patientInfo) return id || "ID non disponible";
    if ('maladeId' in patientInfo && patientInfo.maladeId) return String(patientInfo.maladeId);
    if ('patientId' in patientInfo && patientInfo.patientId) return String(patientInfo.patientId);
    return id || "ID non disponible";
  };

  // Helper to get patient phone
  const getPatientPhone = (): string => {
    if (!patientInfo) return "Non spécifié";
    return (
      ("telephone" in patientInfo && patientInfo.telephone) ||
      ("phone" in patientInfo && patientInfo.phone) ||
      "Non spécifié"
    );
  };

  // Helper to get financial information
  interface FinancialInfo {
    total: number;
    paid: number;
    due: number;
  }

  const getFinancialInfo = (): FinancialInfo | null => {
    if (!patientInfo) {
      return null;
    }

    if (
      "montant" in patientInfo &&
      typeof patientInfo.montant === "number" &&
      "sommePaye" in patientInfo &&
      typeof patientInfo.sommePaye === "number" &&
      "sommeImpaye" in patientInfo &&
      typeof patientInfo.sommeImpaye === "number"
    ) {
      return {
        total: patientInfo.montant || 0,
        paid: patientInfo.sommePaye || 0,
        due: patientInfo.sommeImpaye || 0,
      };
    }

    if (
      "consultationFee" in (patientInfo as any) &&
      "amountPaid" in (patientInfo as any) &&
      "amountDue" in (patientInfo as any)
    ) {
      const p = patientInfo as any;
      return {
        total: p.consultationFee || 0,
        paid: p.amountPaid || 0,
        due: p.amountDue || 0,
      };
    }

    if (medicalRecords.length > 0) {
      return medicalRecords.reduce(
        (acc, record) => ({
          total: acc.total + (record.consultationFee || 0),
          paid: acc.paid + (record.amountPaid || 0),
          due: acc.due + (record.amountDue || 0),
        }),
        { total: 0, paid: 0, due: 0 }
      );
    }

    return null;
  };

  const financialInfo = getFinancialInfo();

  const pdfAdresse = (() => {
    if (patientInfo) {
      if ("address" in patientInfo) return (patientInfo as any).address || "";
      if ("adresse" in patientInfo) return (patientInfo as any).adresse || "";
    }
    return "";
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 print:p-0">
      <Header
        title="Fiche Patient"
        description={`Détails du patient ${getPatientName()}`}
        action={{
          label: "Retour",
          to: backPath,
          icon: <ArrowLeft className="w-4 h-4" />,
        }}
        role="admin"
      />
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 print:shadow-none print:p-4">
          {/* Header with actions */}
          <div className="flex justify-between items-center mb-6 print:hidden">
            <h3 className="text-xl font-semibold">{getPatientName()}</h3>
            <div className="flex items-center gap-3">
              <PDFDownloadLink
                document={
                  <PatientPDF
                    patientInfo={{
                      id: parseInt(getPatientId()) || 0,
                      fullName: getPatientName(),
                      dateOfBirth:
                        (patientInfo && "dateOfBirth" in patientInfo)
                          ? String(patientInfo.dateOfBirth)
                          : "",
                      dateVisit:
                        (patientInfo && "dateVisit" in patientInfo)
                          ? String(patientInfo.dateVisit)
                          : new Date().toISOString().slice(0, 10),
                      motif:
                        (patientInfo && "motif" in patientInfo)
                          ? String(patientInfo.motif)
                          : "",
                      doctorName:
                        (patientInfo && "doctorName" in patientInfo)
                          ? String(patientInfo.doctorName)
                          : "",
                      montant:
                        (patientInfo && "montant" in patientInfo)
                          ? Number(patientInfo.montant)
                          : 0,
                      sommePaye:
                        (patientInfo && "sommePaye" in patientInfo)
                          ? Number(patientInfo.sommePaye)
                          : 0,
                      sommeImpaye:
                        (patientInfo && "sommeImpaye" in patientInfo)
                          ? Number(patientInfo.sommeImpaye)
                          : 0,
                      telephone: getPatientPhone(),
                      adresse: String(pdfAdresse),
                    }}
                  />
                }
                fileName={`patient_${getPatientName().replace(
                  /\s+/g,
                  "_"
                )}_${getPatientId()}.pdf`}
                className="flex items-center gap-2 px-3 py-2 bg-white text-blue-600 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Télécharger PDF</span>
              </PDFDownloadLink>
              <button
                onClick={() => navigate(editPath)}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 hover:text-yellow-800 transition-colors"
              >
                <Edit size={16} />
                <span className="text-sm">Modifier</span>
              </button>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg print:border print:bg-white print:p-3">
              <h4 className="text-lg font-semibold text-gray-800 mb-3 print:text-base">
                Informations Personnelles
              </h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 print:text-xs">
                    Nom complet
                  </p>
                  <p className="font-medium">{getPatientName()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 print:text-xs">
                    Date de naissance
                  </p>
                  <p className="font-medium">
                    {formatDate(
                      (patientInfo && "dateOfBirth" in patientInfo)
                        ? (patientInfo as any).dateOfBirth
                        : null
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 print:text-xs">
                    Téléphone
                  </p>
                  <p className="font-medium">{getPatientPhone()}</p>
                </div>
                {patientEmail && (
                  <div>
                    <p className="text-sm text-gray-500 print:text-xs">Email</p>
                    <p className="font-medium">{patientEmail}</p>
                  </div>
                )}
                {patientGender && (
                  <div>
                    <p className="text-sm text-gray-500 print:text-xs">Sexe</p>
                    <p className="font-medium">{patientGender}</p>
                  </div>
                )}
                {patientAddress && (
                  <div>
                    <p className="text-sm text-gray-500 print:text-xs">
                      Adresse
                    </p>
                    <p className="font-medium">{patientAddress}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Information */}
            {financialInfo && (
              <div className="bg-blue-50 p-4 rounded-lg print:border print:bg-white print:p-3">
                <h4 className="text-lg font-semibold text-gray-800 mb-3 print:text-base">
                  Informations Financières
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 print:text-xs">
                      Montant total
                    </p>
                    <p className="font-medium">
                      {financialInfo.total.toLocaleString("fr-FR")} MRU
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 print:text-xs">
                      Somme payée
                    </p>
                    <p className="font-medium text-green-600">
                      {financialInfo.paid.toLocaleString("fr-FR")} MRU
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 print:text-xs">
                      Somme impayée
                    </p>
                    <p className="font-medium text-red-600">
                      {financialInfo.due.toLocaleString("fr-FR")} MRU
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Consultation History */}
          {consultations.length > 0 && (
            <div className="space-y-6 mt-6">
              <h3 className="text-xl font-semibold border-b pb-2">
                Historique des consultations
              </h3>
              {consultations.map((c) => (
                <div
                  key={c.id}
                  className="border rounded-lg p-4 bg-white space-y-2 print:border-gray-300 print:bg-white print:p-3"
                >
                  <p>
                    <strong>Date :</strong>{" "}
                    {formatDate(c.dateVisit || c.examinationDate)}
                  </p>
                  <p>
                    <strong>Motif :</strong> {c.motif || "Non spécifié"}
                  </p>
                  <p>
                    <strong>Médecin :</strong>{" "}
                    {c.doctorName || `ID: ${c.doctorId}`}
                  </p>
                  {c.montant !== undefined && (
                    <p>
                      <strong>Montant :</strong>{" "}
                      {c.montant.toLocaleString("fr-FR")} MRU
                    </p>
                  )}
                  {c.sommePaye !== undefined && (
                    <p>
                      <strong>Payé :</strong>{" "}
                      {c.sommePaye.toLocaleString("fr-FR")} MRU
                    </p>
                  )}
                  {c.sommeImpaye !== undefined && (
                    <p>
                      <strong>Impayé :</strong>{" "}
                      {c.sommeImpaye.toLocaleString("fr-FR")} MRU
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Medical Constants */}
          {constantes.length > 0 && (
            <div className="space-y-6 mt-10">
              <h3 className="text-xl font-semibold border-b pb-2">
                Constantes médicales
              </h3>
              {constantes.map((c) => (
                <div
                  key={c.id}
                  className="border rounded-lg p-4 bg-white space-y-2 print:border-gray-300 print:bg-white print:p-3"
                >
                  <p>
                    <strong>Date :</strong>{" "}
                    {formatDate(c.dateVisit || c.date || c.createdAt)}
                  </p>
                  <p>
                    <strong>Poids :</strong>{" "}
                    {(c.poids || c.weight) ?? "Non spécifié"}{" "}
                    {c.poids || c.weight ? "kg" : ""}
                  </p>
                  <p>
                    <strong>Taille :</strong>{" "}
                    {(c.taille || c.height) ?? "Non spécifié"}{" "}
                    {c.taille || c.height ? "m" : ""}
                  </p>
                  <p>
                    <strong>Tension artérielle :</strong>{" "}
                    {c.tensionArterielle || c.bloodPressure || "Non spécifié"}
                  </p>
                  <p>
                    <strong>Fréquence cardiaque :</strong>{" "}
                    {(c.frequenceCardiaque || c.heartRate) ?? "Non spécifié"}{" "}
                    {c.frequenceCardiaque || c.heartRate ? "bpm" : ""}
                  </p>
                  <p>
                    <strong>Température :</strong>{" "}
                    {c.temperature ?? "Non spécifié"}{" "}
                    {c.temperature ? "°C" : ""}
                  </p>
                  <p>
                    <strong>Motif :</strong>{" "}
                    {c.motif || c.reason || "Non spécifié"}
                  </p>
                  {c.status && (
                    <p>
                      <strong>Status :</strong> {c.status}
                    </p>
                  )}
                  <p>
                    <strong>Médecin :</strong> {c.doctorName || "Non spécifié"}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Medical Records */}
          {medicalRecords.length > 0 && (
            <div className="space-y-6 mt-10">
              <h3 className="text-xl font-semibold border-b pb-2">
                Dossiers médicaux
              </h3>
              {medicalRecords.map((record) => (
                <div
                  key={record.id}
                  className="border rounded-lg p-6 bg-gray-50 space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-semibold text-blue-700 mb-2">
                        Informations Patient
                      </h4>
                      <p>
                        <strong>Nom :</strong>{" "}
                        {record.patientFullName || getPatientName()}
                      </p>
                      <p>
                        <strong>Téléphone :</strong>{" "}
                        {record.phone || getPatientPhone()}
                      </p>
                      <p>
                        <strong>Date de création :</strong>{" "}
                        {formatDate(record.dateCreation)}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-blue-700 mb-2">
                        Médecin
                      </h4>
                      <p>
                        <strong>Nom du Médecin :</strong>{" "}
                        {record.doctorName || "Non spécifié"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-semibold text-blue-700 mb-2">
                        Symptômes
                      </h4>
                      <p>
                        <strong>Symptôme :</strong>{" "}
                        {record.symptoms || "Non spécifié"}
                      </p>
                      <p>
                        <strong>Durée :</strong>{" "}
                        {record.symptomDuration || "Non spécifié"}
                      </p>
                      <p>
                        <strong>Médicaments actuels :</strong>{" "}
                        {record.currentMedication || "Non spécifié"}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-blue-700 mb-2">
                        Antécédents
                      </h4>
                      <p>
                        <strong>Conditions préexistantes :</strong>{" "}
                        {record.preexistingCondition || "Non spécifié"}
                      </p>
                      <p>
                        <strong>Antécédents familiaux :</strong>{" "}
                        {record.familyHistory || "Non spécifié"}
                      </p>
                      <p>
                        <strong>Allergies :</strong>{" "}
                        {record.allergies || "Non spécifié"}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-700 mb-2">
                      Évaluation
                    </h4>
                    <p>
                      <strong>Évaluation :</strong>{" "}
                      {record.assessment || "Non spécifié"}
                    </p>
                    <p>
                      <strong>Recommandation :</strong>{" "}
                      {record.recommendation || "Non spécifié"}
                    </p>
                    {record.diagnostic && (
                      <p>
                        <strong>Diagnostic :</strong> {record.diagnostic}
                      </p>
                    )}
                  </div>
                  {(record.consultationFee !== undefined ||
                    record.amountPaid !== undefined ||
                    record.amountDue !== undefined) && (
                      <div>
                        <h4 className="text-lg font-semibold text-blue-700 mb-2">
                          Informations Financières
                        </h4>
                        {record.consultationFee !== undefined && (
                          <p>
                            <strong>Montant total :</strong>{" "}
                            {record.consultationFee.toLocaleString("fr-FR")} MRU
                          </p>
                        )}
                        {record.amountPaid !== undefined && (
                          <p>
                            <strong>Somme payée :</strong>{" "}
                            <span className="text-green-600">
                              {record.amountPaid.toLocaleString("fr-FR")} MRU
                            </span>
                          </p>
                        )}
                        {record.amountDue !== undefined && (
                          <p>
                            <strong>Somme impayée :</strong>{" "}
                            <span className="text-red-600">
                              {record.amountDue.toLocaleString("fr-FR")} MRU
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                  {(record.prescriptionUrl || record.resultUrl) && (
                    <div>
                      <h4 className="text-lg font-semibold text-blue-700 mb-2">
                        Fichiers médicaux
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="font-medium mb-1">Prescription</p>
                          {renderFilePreview(record.prescriptionUrl || null)}
                        </div>
                        <div>
                          <p className="font-medium mb-1">Résultat</p>
                          {renderFilePreview(record.resultUrl || null)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientAccountView;
